<template>
  <div class="companies">
    <SectionHeader>
      <template v-slot:breadcrumbs>
        <BreadCrumbs
          :items="[{ text: $t('routes.events'), name: 'company_routes' }, { text: routeEvent.event_date_formatted }]"
        />
      </template>
      <template v-slot:meta>
        <button @click="deleteEvent" class="button button-red button-round button-small">
          {{ $t('routes.deleteEvent') }}
        </button>
        <button @click="createRoute" class="button button-green button-round button-small" :disabled="isCreating">
          {{ createButtonText }}
        </button>
      </template>
    </SectionHeader>

    <div class="body-content cf">
      <CompanyNav />
      <div class="body">
        <div class="content-wrap">
          <header class="content-header cf">
            <h3>{{ $t('meta.overview') }}</h3>
          </header>
          <div class="content-body event-overview">
            <div>
              {{ `${$t('meta.date')}: ${routeEvent.event_date_formatted}` }}
            </div>
            <div v-if="routeEvent.vehicles" class="vehicles">
              {{ $t('meta.vehicles') }}:
              <div class="vehicles-list">
                <Link
                  :to="`/company/vehicles/${vehicle.uuid}`"
                  v-for="vehicle in routeEvent.vehicles"
                  :key="vehicle.uuid"
                  >{{ vehicle && vehicle.name }}
                </Link>
              </div>
            </div>
            <div v-if="routeEvent.template">
              {{ $t('routes.template') }}:
              <Link :to="`/company/route_templates/${routeEvent.template.uuid}`">{{
                routeEvent.template && routeEvent.template.name
              }}</Link>
            </div>
            <div v-if="templateType">
              {{ `${$t('routes.templateType')}: ${$t(`routes.template${capitalizeCamelCase(templateType)}`)}` }}
            </div>
          </div>
        </div>

        <div v-if="routeEvent.routes && routeEvent.routes.length > 0" class="content-wrap">
          <header class="content-header cf">
            <h3>{{ $t('nav.routes') }}</h3>
          </header>
          <div class="content-body">
            <Table data-testid="route-events-show-table">
              <template #header>
                <th>{{ $t('meta.name') }}</th>
                <th>
                  <span class="visually-hidden">{{ $t('meta.actions') }}</span>
                </th>
              </template>
              <template #body>
                <tr v-for="route in availableRoutes" :key="route.uuid">
                  <td>
                    <Link :to="`/company/routes/${route.uuid}`">{{ route && route.name }}</Link>
                  </td>
                  <td class="table__actions">
                    <Link :to="`/company/routes/${route.uuid}`" class="table__action-button"
                      ><span class="material-icons">edit</span></Link
                    >
                    <Link :to="`/routes/${route.uuid}`" class="table__action-button"
                      ><span class="material-icons">launch</span></Link
                    >
                  </td>
                </tr>
              </template>
            </Table>
          </div>
        </div>

        <div class="content-wrap" v-if="itLoadsContainers">
          <header class="content-header cf">
            <h3>
              {{ capitalize($t('routes.routeEvents.containersToBeEmptied')) }}
            </h3>
          </header>
          <div class="content-body">
            <LoadingSpinner v-if="loading" />
            <div v-else>
              <p class="no-margin">{{ $t('routes.containersCount') }}: {{ containers.length }}</p>
              <Table class="table-containers">
                <template #header>
                  <th>{{ $t('meta.container') }}</th>
                  <th>{{ $t('meta.location') }}</th>
                  <th>{{ $t('meta.type') }}</th>
                </template>
                <template #body>
                  <tr v-for="container in containers" :key="container.uuid">
                    <td>{{ container.name }}</td>
                    <td>{{ container.preformatted }}</td>
                    <td>{{ container.type_name }}</td>
                  </tr>
                </template>
              </Table>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'

import BreadCrumbs from '@/components/BreadCrumbs'
import LoadingSpinner from '@/components/LoadingSpinner'
import SectionHeader from '@/components/SectionHeader'
import Table from '@/components/Table/index.vue'
import CompanyNav from '@/components/company_nav'
import dateUtils from '@/components/mixins/dateUtils'
import stringUtils from '@/components/mixins/stringUtils'

import routeEventsApi from '@/api/route_events/index'

export default {
  name: 'company_route_events',
  mixins: [stringUtils, dateUtils],
  inject: ['showPopup'],
  data: () => ({
    loading: true,
    routeEvent: {},
    containers: [],
  }),
  components: { BreadCrumbs, CompanyNav, LoadingSpinner, SectionHeader, Table },
  computed: {
    ...mapGetters(['creatingRouteInfo']),
    templateType() {
      return this.routeEvent?.template?.template_type
    },
    createButtonText() {
      return this.isCreating
        ? this.$t('routes.routeEvents.creatingSince', {
            datetime: this.creatingDate,
          })
        : `${this.$t('routes.createNow')}`
    },
    itLoadsContainers() {
      return this.templateType === 'static'
    },
    creatingInfo() {
      return this.creatingRouteInfo[this.routeEvent.uuid]
    },
    isCreating() {
      return this.creatingInfo?.creating
    },
    creatingDate() {
      return this.formattedFromDateTime(this.creatingInfo?.generatedAt)
    },
    availableRoutes() {
      return this.routeEvent.routes.filter(route => ['available', 'success'].includes(route.status))
    },
  },
  methods: {
    async getData() {
      this.routeEvent = await routeEventsApi.get(this.$route.params.event_uuid)
      this.getContainers()
    },
    async getContainers() {
      if (this.itLoadsContainers) {
        this.containers = await routeEventsApi.getContainers(this.$route.params.event_uuid)
      }
      this.loading = false
    },
    async deleteEvent() {
      if (await this.showPopup({ type: 'confirm', content: this.$t('routes.routeEvents.deleteConfirmation') })) {
        await routeEventsApi.delete(this.$route.params.event_uuid)
        this.$router.push(`/company/routes`)
      }
    },
    async checkRouteStatus() {
      await this.$store.dispatch('CHECK_ROUTE_STATUS', {
        eventUuid: this.routeEvent?.uuid,
      })
    },
    async createRoute() {
      if (this.isCreating) return

      this.loading = true
      const result = await routeEventsApi.createRoute(this.$route.params.event_uuid)

      if (result.success) this.checkRouteStatus()

      this.loading = false
      this.getData()
    },
  },
  async mounted() {
    await this.getData()
    this.checkRouteStatus()
  },
}
</script>

<style lang="scss" scoped>
.event-overview {
  display: flex;
  justify-content: space-between;

  > div {
    flex: 2;
  }

  .vehicles {
    display: flex;
    flex: 3;
    gap: 0.5rem;

    &-list {
      display: flex;
      flex-direction: column;
      gap: 0.25rem;
    }
  }
}
.table-containers {
  margin-top: 1.25rem;
}
</style>
